import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { PhotoSlot } from "@shyne/shared/api";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { appointment_status, AppointmentClockData, AppointmentExtraPhoto } from "@/models/appointment.model";
import { Upload, Camera, CheckCircle2, Image as ImageIcon, Loader2, Clock, Lock, Plus, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { ImageLightbox } from "@/components/shared/image-lightbox";
import { optimizeImage, formatFileSize } from "@/lib/image-optimizer";
import {
  getAfterPhotoLockMessage,
  getBeforePhotoLockMessage,
  getDistinctWorkflowDescription,
  getWorkflowPhoto,
} from "@/lib/utils/workflow-photo-gate";
import { toast } from "sonner";
import moment from "moment";

export type OnPhotoUpload = (
  workflowId: string,
  file: File,
  isAfter: boolean,
  options?: { isCapture?: boolean }
) => void;

// optimizeImage compresses anything reasonable down to ~2MB before upload;
// this cap only rejects absurd selections that would stall the in-browser
// optimizer, so it must stay far above what phone cameras produce.
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

interface PhotoUploadSectionProps {
  serviceWorkflows: PhotoSlot[];
  appointmentWorkflows: AppointmentWorkflow[];
  onPhotoUpload: OnPhotoUpload;
  readOnly?: boolean;
  // Appointment status drives the flow-order gates (start work -> before ->
  // begin tasks -> checklist -> after). When omitted (read-only detail views)
  // no gating is applied — the section is display-only there anyway.
  status?: string;
  // "Begin Tasks" phase marker: locks the before photos (upload AND replace)
  // and is a prerequisite for the after photos.
  workStarted?: boolean;
  checklistComplete?: boolean;
  // Distinguishes "checklist incomplete" (locked) from "checklist still
  // loading / failed to load" so the lock hint never tells a worker to
  // complete tasks that are already complete.
  checklistLoaded?: boolean;
  clockInData?: AppointmentClockData;
  clockOutData?: AppointmentClockData;
  // Ad-hoc photos beyond the required slots (meta_data.extra_photos). The
  // add/remove handlers are omitted on read-only detail views.
  extraPhotos?: AppointmentExtraPhoto[];
  onAddExtraPhoto?: (file: File, isAfter: boolean) => void | Promise<void>;
  onRemoveExtraPhoto?: (url: string) => void | Promise<void>;
}

export function PhotoUploadSection({
  serviceWorkflows,
  appointmentWorkflows,
  onPhotoUpload,
  readOnly = false,
  status,
  workStarted = false,
  checklistComplete = true,
  checklistLoaded = true,
  clockInData,
  clockOutData,
  extraPhotos = [],
  onAddExtraPhoto,
  onRemoveExtraPhoto,
}: PhotoUploadSectionProps) {
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(new Set());

  // Expand all workflows by default
  useEffect(() => {
    if (serviceWorkflows.length > 0) {
      const allIds = new Set(serviceWorkflows.map((wf) => wf.id));
      if (clockInData?.photoUrl) allIds.add("__clock_in__");
      if (clockOutData?.photoUrl) allIds.add("__clock_out__");
      setExpandedWorkflows(allIds);
    }
  }, [serviceWorkflows, clockInData, clockOutData]);
  const [uploading, setUploading] = useState<Set<string>>(new Set());
  const [uploadingExtra, setUploadingExtra] = useState<"before" | "after" | null>(null);
  const extraBeforeInputRef = useRef<HTMLInputElement | null>(null);
  const extraAfterInputRef = useRef<HTMLInputElement | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string>("");
  const [lightboxAlt, setLightboxAlt] = useState<string>("");
  const fileInputRefs = useRef<{ [key: string]: { before: HTMLInputElement | null; after: HTMLInputElement | null } }>(
    {}
  );

  const openLightbox = (imageUrl: string, alt: string) => {
    setLightboxImage(imageUrl);
    setLightboxAlt(alt);
    setLightboxOpen(true);
  };

  const toggleWorkflow = (workflowId: string) => {
    const newExpanded = new Set(expandedWorkflows);
    if (newExpanded.has(workflowId)) {
      newExpanded.delete(workflowId);
    } else {
      newExpanded.add(workflowId);
    }
    setExpandedWorkflows(newExpanded);
  };

  const getWorkflowStatus = (workflowId: string) => {
    const beforePhoto = appointmentWorkflows.find((aw) => aw.workflow === workflowId && !aw.is_after);
    const afterPhoto = appointmentWorkflows.find((aw) => aw.workflow === workflowId && aw.is_after);

    return { beforePhoto, afterPhoto };
  };

  const beforeLockMessage = status !== undefined ? getBeforePhotoLockMessage(status, workStarted) : null;
  const getAfterLockMessage = (workflowId: string) => {
    if (status === undefined) return null;
    return getAfterPhotoLockMessage({
      status,
      workStarted,
      hasBeforePhoto: !!getWorkflowPhoto(appointmentWorkflows, workflowId, false),
      checklistLoaded,
      checklistComplete,
    });
  };
  const isBeforeReplacementBlocked =
    status !== undefined && (status !== appointment_status.inprogress || workStarted);
  const isAfterReplacementBlocked = status !== undefined && status !== appointment_status.inprogress;

  const isUploadBlocked = (workflowId: string, isAfter: boolean) => {
    const hasPhoto = !!getWorkflowPhoto(appointmentWorkflows, workflowId, isAfter);
    if (hasPhoto) return isAfter ? isAfterReplacementBlocked : isBeforeReplacementBlocked;
    return isAfter ? getAfterLockMessage(workflowId) !== null : beforeLockMessage !== null;
  };

  // Additional (extra) photos follow the same phase windows: before extras
  // during the before-photo review, after extras during the after-photo phase.
  const canAddExtraBefore =
    !readOnly && !!onAddExtraPhoto && status === appointment_status.inprogress && !workStarted;
  const canAddExtraAfter =
    !readOnly &&
    !!onAddExtraPhoto &&
    status === appointment_status.inprogress &&
    workStarted &&
    checklistLoaded &&
    checklistComplete;
  const canRemoveExtra = (photo: AppointmentExtraPhoto) =>
    !readOnly &&
    !!onRemoveExtraPhoto &&
    status === appointment_status.inprogress &&
    (photo.is_after ? workStarted : !workStarted);

  const handleCameraCapture = async (workflowId: string, isAfter: boolean) => {
    if (readOnly) return;
    if (isUploadBlocked(workflowId, isAfter)) return;

    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Use back camera if available
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      // Create video element to show camera feed
      const video = document.createElement("video");
      video.srcObject = stream;
      video.play();

      // Create a modal-like overlay for camera capture
      const overlay = document.createElement("div");
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      `;

      const videoContainer = document.createElement("div");
      videoContainer.style.cssText = `
        position: relative;
        max-width: 90%;
        max-height: 80%;
        border-radius: 8px;
        overflow: hidden;
      `;

      const controls = document.createElement("div");
      controls.style.cssText = `
        display: flex;
        gap: 16px;
        margin-top: 16px;
      `;

      const captureBtn = document.createElement("button");
      captureBtn.textContent = "Capture Photo";
      captureBtn.style.cssText = `
        background: #10b981;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-size: 16px;
        cursor: pointer;
      `;

      const cancelBtn = document.createElement("button");
      cancelBtn.textContent = "Cancel";
      cancelBtn.style.cssText = `
        background: #ef4444;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-size: 16px;
        cursor: pointer;
      `;

      controls.appendChild(captureBtn);
      controls.appendChild(cancelBtn);
      videoContainer.appendChild(video);
      overlay.appendChild(videoContainer);
      overlay.appendChild(controls);
      document.body.appendChild(overlay);

      // Handle capture
      const capturePhoto = () => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        if (context) {
          context.drawImage(video, 0, 0);

          canvas.toBlob(
            async (blob) => {
              if (blob) {
                const key = `${workflowId}-${isAfter ? "after" : "before"}`;
                setUploading((prev) => new Set(prev).add(key));
                try {
                  const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
                    type: "image/jpeg",
                  });
                  const optimizedFile = await optimizeImage(file);
                  await Promise.resolve(onPhotoUpload(workflowId, optimizedFile, isAfter, { isCapture: true }));
                } catch (error) {
                  console.error("Failed to upload captured image:", error);
                  toast.error(error instanceof Error ? error.message : "Failed to upload photo");
                } finally {
                  setUploading((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                  });
                }
              }
            },
            "image/jpeg",
            0.9
          );
        }

        // Cleanup
        stream.getTracks().forEach((track) => track.stop());
        document.body.removeChild(overlay);
      };

      // Handle cancel
      const cancelCapture = () => {
        stream.getTracks().forEach((track) => track.stop());
        document.body.removeChild(overlay);
      };

      captureBtn.addEventListener("click", capturePhoto);
      cancelBtn.addEventListener("click", cancelCapture);
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert("Unable to access camera. Please ensure camera permissions are granted.");
    }
  };

  const handleFileSelect = async (workflowId: string, isAfter: boolean, event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    if (isUploadBlocked(workflowId, isAfter)) return;
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File is too large to upload (${formatFileSize(MAX_UPLOAD_BYTES)} max)`);
      return;
    }
    const key = `${workflowId}-${isAfter ? "after" : "before"}`;
    setUploading((prev) => new Set(prev).add(key));
    event.target.value = "";
    try {
      const originalSize = formatFileSize(file.size);
      const optimizedFile = await optimizeImage(file);
      const optimizedSize = formatFileSize(optimizedFile.size);

      if (optimizedFile.size < file.size * 0.8) {
        toast.info(`Image optimized: ${originalSize} → ${optimizedSize}`);
      }

      await Promise.resolve(onPhotoUpload(workflowId, optimizedFile, isAfter));
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error(error instanceof Error ? error.message : "Failed to upload photo");
    } finally {
      setUploading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleExtraFileSelect = async (isAfter: boolean, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !onAddExtraPhoto) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File is too large to upload (${formatFileSize(MAX_UPLOAD_BYTES)} max)`);
      return;
    }
    setUploadingExtra(isAfter ? "after" : "before");
    try {
      const optimizedFile = await optimizeImage(file);
      await Promise.resolve(onAddExtraPhoto(optimizedFile, isAfter));
    } catch (error) {
      console.error("Failed to upload extra photo:", error);
      toast.error(error instanceof Error ? error.message : "Failed to upload photo");
    } finally {
      setUploadingExtra(null);
    }
  };

  const triggerFileInput = (workflowId: string, isAfter: boolean) => {
    if (readOnly) return;
    if (isUploadBlocked(workflowId, isAfter)) return;
    const key = isAfter ? "after" : "before";
    fileInputRefs.current[workflowId]?.[key]?.click();
  };

  const getUploadButtonText = (workflowId: string, isAfter: boolean) => {
    const uploadingKey = `${workflowId}-${isAfter ? "after" : "before"}`;
    if (uploading.has(uploadingKey)) {
      return "Uploading...";
    }
    return isAfter ? "Capture After Photo" : "Capture Before Photo";
  };

  const formatPhotoTime = (dateString: string) => {
    return moment(dateString).format("MM/DD/YYYY, h:mm a");
  };

  if (
    serviceWorkflows.length === 0 &&
    extraPhotos.length === 0 &&
    !clockInData?.photoUrl &&
    !clockOutData?.photoUrl
  ) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Camera className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No photo requirements defined for this service.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Photo Requirements Overview */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Photo Documentation Progress</h3>
          <Badge variant="secondary">
            {appointmentWorkflows.length} of {serviceWorkflows.length * 2} photos uploaded
          </Badge>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width:
                serviceWorkflows.length > 0
                  ? `${(appointmentWorkflows.length / (serviceWorkflows.length * 2)) * 100}%`
                  : "0%",
            }}
          />
        </div>
      </div>

      {/* Start Work Photo */}
      {clockInData?.photoUrl && (
        <Card className="overflow-hidden">
          <CardHeader
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => toggleWorkflow("__clock_in__")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-600" />
                Start Work Photo
              </CardTitle>
              <Badge variant="default">
                {moment(clockInData.time).format("h:mm a")}
              </Badge>
            </div>
          </CardHeader>
          {expandedWorkflows.has("__clock_in__") && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <img
                    src={clockInData.photoUrl}
                    alt="Start work photo"
                    className="max-w-full max-h-48 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openLightbox(clockInData.photoUrl, "Start work photo")}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatPhotoTime(clockInData.time)}
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Photo Workflows */}
      <div className="space-y-3">
        {serviceWorkflows.map((workflow) => {
          const isExpanded = expandedWorkflows.has(workflow.id);
          const { beforePhoto, afterPhoto } = getWorkflowStatus(workflow.id);
          const hasBeforePhoto = !!beforePhoto;
          const hasAfterPhoto = !!afterPhoto;
          const workflowDescription = getDistinctWorkflowDescription(workflow);

          return (
            <Card key={workflow.id} className="overflow-hidden">
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleWorkflow(workflow.id)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Camera className="w-4 h-4 text-primary" />
                    {workflow.title}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={hasBeforePhoto ? "default" : "secondary"}>
                      Before {hasBeforePhoto ? "✓" : "○"}
                    </Badge>
                    <Badge variant={hasAfterPhoto ? "default" : "secondary"}>After {hasAfterPhoto ? "✓" : "○"}</Badge>
                  </div>
                </div>
                {workflowDescription && <p className="text-sm text-muted-foreground mt-1">{workflowDescription}</p>}
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0">
                  {!readOnly && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => {
                          if (!fileInputRefs.current[workflow.id]) fileInputRefs.current[workflow.id] = { before: null, after: null };
                          fileInputRefs.current[workflow.id].before = el;
                        }}
                        onChange={(e) => handleFileSelect(workflow.id, false, e)}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => {
                          if (!fileInputRefs.current[workflow.id]) fileInputRefs.current[workflow.id] = { before: null, after: null };
                          fileInputRefs.current[workflow.id].after = el;
                        }}
                        onChange={(e) => handleFileSelect(workflow.id, true, e)}
                      />
                    </>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Before Photo */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Before Photo</Label>
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center">
                        {beforePhoto ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <p className="text-sm text-green-600 font-medium">Photo uploaded</p>
                            <div className="flex items-center justify-center">
                              <img
                                src={beforePhoto.image}
                                alt="Before photo"
                                className="max-w-full max-h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openLightbox(beforePhoto.image, "Before photo")}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatPhotoTime(beforePhoto.created_at)}
                            </p>
                            {!readOnly &&
                              (beforeLockMessage ? (
                                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                  <Lock className="w-3 h-3" />
                                  {beforeLockMessage}
                                </p>
                              ) : (
                                <div className="flex flex-wrap gap-2 justify-center">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCameraCapture(workflow.id, false)}
                                    disabled={uploading.has(`${workflow.id}-before`)}
                                  >
                                    {uploading.has(`${workflow.id}-before`) ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Camera className="w-4 h-4 mr-2" />
                                    )}
                                    {uploading.has(`${workflow.id}-before`) ? "Processing..." : "Replace (Camera)"}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => triggerFileInput(workflow.id, false)}
                                    disabled={uploading.has(`${workflow.id}-before`)}
                                  >
                                    {uploading.has(`${workflow.id}-before`) ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Upload className="w-4 h-4 mr-2" />
                                    )}
                                    {uploading.has(`${workflow.id}-before`) ? "Processing..." : "Replace (Upload)"}
                                  </Button>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No before photo uploaded</p>
                            {!readOnly &&
                              (beforeLockMessage ? (
                                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                  <Lock className="w-3 h-3" />
                                  {beforeLockMessage}
                                </p>
                              ) : (
                                <div className="flex flex-wrap gap-2 justify-center">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCameraCapture(workflow.id, false)}
                                    disabled={uploading.has(`${workflow.id}-before`)}
                                  >
                                    {uploading.has(`${workflow.id}-before`) ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Camera className="w-4 h-4 mr-2" />
                                    )}
                                    {uploading.has(`${workflow.id}-before`) ? "Processing..." : "Capture"}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => triggerFileInput(workflow.id, false)}
                                    disabled={uploading.has(`${workflow.id}-before`)}
                                  >
                                    {uploading.has(`${workflow.id}-before`) ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Upload className="w-4 h-4 mr-2" />
                                    )}
                                    {uploading.has(`${workflow.id}-before`) ? "Processing..." : "Upload"}
                                  </Button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* After Photo */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">After Photo</Label>
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center">
                        {afterPhoto ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <p className="text-sm text-green-600 font-medium">Photo uploaded</p>
                            <div className="flex items-center justify-center">
                              <img
                                src={afterPhoto.image}
                                alt="After photo"
                                className="max-w-full max-h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openLightbox(afterPhoto.image, "After photo")}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatPhotoTime(afterPhoto.created_at)}
                            </p>
                            {!readOnly && (
                              <div className="flex flex-wrap gap-2 justify-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCameraCapture(workflow.id, true)}
                                  disabled={uploading.has(`${workflow.id}-after`) || isAfterReplacementBlocked}
                                >
                                  {uploading.has(`${workflow.id}-after`) ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Camera className="w-4 h-4 mr-2" />
                                  )}
                                  {uploading.has(`${workflow.id}-after`) ? "Processing..." : "Replace (Camera)"}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => triggerFileInput(workflow.id, true)}
                                  disabled={uploading.has(`${workflow.id}-after`) || isAfterReplacementBlocked}
                                >
                                  {uploading.has(`${workflow.id}-after`) ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  {uploading.has(`${workflow.id}-after`) ? "Processing..." : "Replace (Upload)"}
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No after photo uploaded</p>
                            {!readOnly &&
                              (!getAfterLockMessage(workflow.id) ? (
                                <div className="flex flex-wrap gap-2 justify-center">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCameraCapture(workflow.id, true)}
                                    disabled={uploading.has(`${workflow.id}-after`)}
                                  >
                                    {uploading.has(`${workflow.id}-after`) ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Camera className="w-4 h-4 mr-2" />
                                    )}
                                    {uploading.has(`${workflow.id}-after`) ? "Processing..." : "Capture"}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => triggerFileInput(workflow.id, true)}
                                    disabled={uploading.has(`${workflow.id}-after`)}
                                  >
                                    {uploading.has(`${workflow.id}-after`) ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Upload className="w-4 h-4 mr-2" />
                                    )}
                                    {uploading.has(`${workflow.id}-after`) ? "Processing..." : "Upload"}
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                  <Lock className="w-3 h-3" />
                                  {getAfterLockMessage(workflow.id)}
                                </p>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Additional (extra) Photos */}
      {(extraPhotos.length > 0 || canAddExtraBefore || canAddExtraAfter) && (
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-primary" />
              Additional Photos
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Optional extra photos beyond the required areas
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {[false, true].map((isAfter) => {
              const photos = extraPhotos.filter((photo) => photo.is_after === isAfter);
              const canAdd = isAfter ? canAddExtraAfter : canAddExtraBefore;
              if (photos.length === 0 && !canAdd) return null;
              const kind = isAfter ? "After" : "Before";

              return (
                <div key={kind} className="space-y-2">
                  <Label className="text-sm font-medium">Additional {kind} Photos</Label>
                  <div className="flex flex-wrap gap-3">
                    {photos.map((photo) => (
                      <div key={photo.url} className="relative">
                        <img
                          src={photo.url}
                          alt={`Additional ${kind.toLowerCase()} photo`}
                          className="w-24 h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openLightbox(photo.url, `Additional ${kind.toLowerCase()} photo`)}
                        />
                        {canRemoveExtra(photo) && (
                          <button
                            onClick={() => onRemoveExtraPhoto?.(photo.url)}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow"
                            title="Remove photo"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    {canAdd && (
                      <Button
                        variant="outline"
                        onClick={() => (isAfter ? extraAfterInputRef : extraBeforeInputRef).current?.click()}
                        disabled={uploadingExtra !== null}
                        className="w-24 h-24 border-dashed flex flex-col items-center justify-center gap-1"
                      >
                        {uploadingExtra === (isAfter ? "after" : "before") ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Plus className="w-5 h-5" />
                        )}
                        <span className="text-xs">Add</span>
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={extraBeforeInputRef}
              onChange={(e) => handleExtraFileSelect(false, e)}
            />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={extraAfterInputRef}
              onChange={(e) => handleExtraFileSelect(true, e)}
            />
          </CardContent>
        </Card>
      )}

      {/* Complete Work Photo */}
      {clockOutData?.photoUrl && (
        <Card className="overflow-hidden">
          <CardHeader
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => toggleWorkflow("__clock_out__")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-600" />
                Complete Work Photo
              </CardTitle>
              <Badge variant="default">
                {moment(clockOutData.time).format("h:mm a")}
              </Badge>
            </div>
          </CardHeader>
          {expandedWorkflows.has("__clock_out__") && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <img
                    src={clockOutData.photoUrl}
                    alt="Completion photo"
                    className="max-w-full max-h-48 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openLightbox(clockOutData.photoUrl, "Completion photo")}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatPhotoTime(clockOutData.time)}
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={lightboxImage}
        alt={lightboxAlt}
      />
    </div>
  );
}
